/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Modal } from '@carbon/react';
import { createPortal } from 'react-dom';

import { Tooltip } from 'primitives';
import { dedicatedModesStore, selectedElementStore } from 'App/Pages/Diagram/stores';
import { currentDiagramStore, projectStore } from 'stores';
import { notificationStore } from 'components/NotificationSystem';
import { buildSlug } from 'utils/buildSlug';
import { CONNECTOR_TEMPLATE } from 'utils/constants';
import { tracingService, trackingService } from 'services';
import { buildPath } from 'utils/build-path';
import { capitalize } from 'utils/capitalize';
import { ConnectorTemplate } from 'icons';

import { saveElementTemplateStore, FIELDS_IDS, DEFAULT_FOCUSED_FIELD } from './save-element-template-store';
import * as Styled from './SaveElementTemplateConfig.styled';
import { getElementTemplateFromElement, hydrateElementTemplateWithProperties } from './utils';
import { TemplateDefinitionFields } from './TemplateDefinitionFields';

/**
 * Component to save the selected element's configuration as a new template.
 * @type {React.FC<{ hasButtonWithExtraTopSpace?: boolean }>}
 * @param {Object} props - Component properties.
 * @param {boolean} [props.hasButtonWithExtraTopSpace=false] - Whether the button has extra top space.
 * @returns {JSX.Element|null} The rendered component or null if no template is available.
 */
export const SaveElementTemplateConfig = observer(function SaveElementTemplateConfig(
  { hasButtonWithExtraTopSpace } = { hasButtonWithExtraTopSpace: false }
) {
  const { selectedElement, needsRefresh } = selectedElementStore;
  const { modeler, state: diagramStoreState } = currentDiagramStore;
  const { fromProcessApplicationId } = projectStore;
  const { project } = diagramStoreState;
  const [showDialog, setShowDialog] = useState(false);
  const [template, setTemplate] = useState(null);
  const { focusedField, isSaving } = saveElementTemplateStore;

  // Set the template from the selected element
  useEffect(() => {
    const resetTemplate = () => {
      setTemplate(null);
      saveElementTemplateStore.reset();
    };

    if (needsRefresh) {
      selectedElementStore.setNeedsRefresh(false);
    }

    if (selectedElement && modeler) {
      const template = getElementTemplateFromElement({
        modeler,
        element: selectedElement
      });

      if (!template) {
        resetTemplate();
        return;
      }

      setTemplate(template);
      saveElementTemplateStore.init(template);
    } else {
      resetTemplate();
    }
  }, [selectedElement, modeler, needsRefresh]);

  const [dialogSize, setDialogSize] = useState('xs');
  const smallHeightBreakpoint = 1000;

  // Set the dialog size based on the screen height,
  // for small screens the dialog should be small and not xsmall
  // to avoid the content being cut off
  useEffect(() => {
    // TODO(philippfromme): should be part of a reusable modal component
    const updateDimension = () => {
      const newDimension = getCurrentDimension();
      const shouldBeSmall = newDimension.height < smallHeightBreakpoint;
      setDialogSize(shouldBeSmall ? 'sm' : 'xs');
    };
    window.addEventListener('resize', updateDimension);

    return () => {
      window.removeEventListener('resize', updateDimension);
    };
  }, []);

  if (!template) {
    return null;
  }

  const openDialog = () => {
    setShowDialog(true);

    const { id: fileId, type } = currentDiagramStore.state.diagram;
    trackingService.trackSaveAsConnectorTemplate({
      connectorId: template.id,
      fileId,
      type
    });
  };

  const closeDialog = () => {
    setShowDialog(false);
    saveElementTemplateStore.setFocusedField(DEFAULT_FOCUSED_FIELD);
  };

  const getTemplateCategory = () => {
    return template.category?.id === 'connectors' ? 'connector' : 'element';
  };

  const validateBeforeSave = () => {
    saveElementTemplateStore.validateField(FIELDS_IDS.TEMPLATE_NAME, saveElementTemplateStore.templateName);
  };

  /**
   * Saves the connector template as a new file in the project.
   */
  const doSaveAs = async () => {
    validateBeforeSave();

    const isValid = !saveElementTemplateStore.hasError(FIELDS_IDS.TEMPLATE_NAME);

    if (!isValid) {
      return;
    }

    saveElementTemplateStore.setIsSaving(true);

    try {
      template.id = saveElementTemplateStore.templateId;
      template.name = saveElementTemplateStore.templateName.trim();
      template.description = saveElementTemplateStore.templateDescription.trim();

      hydrateElementTemplateWithProperties({ template, element: selectedElement });

      const payload = {
        name: template.name,
        projectId: project.id,
        content: JSON.stringify(template, undefined, 2)
      };

      const file = await projectStore.createFile({
        type: CONNECTOR_TEMPLATE,
        file: payload,
        source: 'save-as',
        isFromTemplate: false
      });

      if (file) {
        const link = buildPath(`/connector-templates/${buildSlug(file.id, file.name)}`);
        notificationStore.success(
          { title: `${capitalize(getTemplateCategory())} template saved` },
          {
            ariaRole: 'status',
            action: {
              label: 'Edit template',
              onClick: () => {
                window.open(link);
              }
            }
          }
        );
      } else {
        throw new Error('File could not be saved.');
      }
    } catch (err) {
      console.error(err);
      tracingService.traceError(err, `Error saving ${getTemplateCategory()} template`);
      notificationStore.error({
        title: `Error saving ${getTemplateCategory()} template`,
        subtitle: 'Please try again later.'
      });
    } finally {
      saveElementTemplateStore.setIsSaving(false);
      closeDialog();
    }
  };

  const SaveAsButton = () => (
    <Tooltip
      showAfter={0}
      align="bottom"
      justify="right"
      title={`Save the configuration as a new ${getTemplateCategory()} template.`}
    >
      <Styled.SaveAsButtonContainer
        onClick={openDialog}
        // @ts-expect-error TS2769
        $extraSpaceOnTop={hasButtonWithExtraTopSpace}
      >
        <>
          Save as <ConnectorTemplate width="16" height="16" />
        </>
      </Styled.SaveAsButtonContainer>
    </Tooltip>
  );

  const SaveAsModal = () =>
    createPortal(
      <Modal
        open={showDialog}
        onRequestClose={closeDialog}
        onRequestSubmit={doSaveAs}
        modalHeading={`Save ${getTemplateCategory()} properties as template`}
        primaryButtonText="Save"
        loadingDescription="Saving..."
        loadingStatus={isSaving ? 'active' : 'inactive'}
        secondaryButtonText="Cancel"
        // @ts-expect-error TS2322
        size={dialogSize}
        selectorPrimaryFocus={`#${focusedField}`}
      >
        <p className="bx--modal-content__text">
          Create a reusable building block by saving your element properties as a new template.
        </p>

        <TemplateDefinitionFields />

        {fromProcessApplicationId && (
          <Styled.InfoWrapper>
            <Styled.InfoIcon size={26} />{' '}
            <div>
              Saving a template inside a process application can complicate versioning. To track and restore this
              template's changes independently, move it out of the process application after saving.
            </div>
          </Styled.InfoWrapper>
        )}
      </Modal>,
      document.body
    );

  if (!dedicatedModesStore.isImplementMode) {
    return null;
  }

  return (
    <>
      <SaveAsButton />
      <SaveAsModal />
    </>
  );
});

function getCurrentDimension() {
  return {
    width: window.innerWidth,
    height: window.innerHeight
  };
}
